import { Facebook, Instagram, Website, Youtube } from "../../../components/Icons";
import { useTranslation } from "../../../hooks/useTranslation";
import style from "./index.module.css";

/**
 * prefix the url with https protocol if it wasn't provided, and if the url is invalid, return an empty string
 */
function getFullUrl(url: string | null | undefined): string {
  if (!url || typeof url !== "string") {
    return "";
  }
  const urlPattern = /^[a-zA-Z]/;
  if (urlPattern.test(url) && !url.startsWith("http://") && !url.startsWith("https://")) {
    url = "https://" + url;
  }
  try {
    return new URL(url).toString();
  } catch {
    return "";
  }
}

const ShowroomSocial = ({ social, about, branches, ...rest }: ComponentPropsType) => {
  const { locale } = useTranslation();

  return (
    <>
      {social && Object.values(social).some((value) => !!value) && (
        <div className={style.section}>
          <h3 className="main_section__title main_section__title--small">{locale("follow_us")}</h3>
          <ul className={style.section_social}>
            {social.fb && getFullUrl(social.fb) && (
              <li>
                <a href={getFullUrl(social.fb)} target="_blank" rel="noreferrer">
                  <Facebook width="37" height="37" color="#000" />
                </a>
              </li>
            )}
            {social.ig && getFullUrl(social.ig) && (
              <li>
                <a href={getFullUrl(social.ig)} target="_blank" rel="noreferrer">
                  <Instagram width="37" height="37" color="#000" />
                </a>
              </li>
            )}
            {social.yt && getFullUrl(social.yt) && (
              <li>
                <a href={getFullUrl(social.yt)} target="_blank" rel="noreferrer">
                  <Youtube width="37" height="37" color="#000" />
                </a>
              </li>
            )}
            {social.website && getFullUrl(social.website) && (
              <li>
                <a href={getFullUrl(social.website)} target="_blank" rel="noreferrer">
                  <Website width="37" height="37" color="#000" />
                </a>
              </li>
            )}
          </ul>
        </div>
      )}
      {!rest.isServiceCenter && branches && branches?.length > 0 && (
        <div className={style.section}>
          <h3 className="main_section__title main_section__title--small">{locale("branches")}</h3>
          <div className={style.section_branches}>
            {branches?.map((branch, index) => (
              <div key={index} className={style.single_branch}>
                <p>
                  <b>{branch.name}:</b> {branch.address}
                </p>
                <p>
                  <a href={`tel:${branch.phone}`} target="_blank" rel="noreferrer">
                    <span>{branch.phone}</span>
                  </a>
                </p>
                <p>
                  <a
                    style={{
                      display: "flex",
                      alignItems: "center",
                    }}
                    href={branch.map_link}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <span></span>
                    {locale("visitUsHere")}
                  </a>
                </p>
              </div>
            ))}
          </div>
        </div>
      )}
      {rest.isServiceCenter ? (
        <div className={style.section}>
          <h3 className="main_section__title main_section__title--small">{locale("location")}</h3>
          <div className={style.section_about}>
            <p>{rest.address}</p>
            <p>
              <b>{locale("city")}:</b> {rest.region.city.name}
            </p>
            <p>
              <b>{locale("region")}:</b> {rest.region.name}
            </p>
          </div>
        </div>
      ) : null}
      {rest.isServiceCenter ? (
        <div className={style.section}>
          <h3 className="main_section__title main_section__title--small">{locale("workHours")}</h3>
          <div className={style.section_about}>
            <p>
              <b>{locale("hours")}:</b> {rest.working_hours}
            </p>
            <p>
              <b>{locale("daysOff")}:</b> {rest.daysOff}
            </p>
          </div>
        </div>
      ) : null}
      {about ? (
        <div className={style.section}>
          <h3 className="main_section__title main_section__title--small">{locale("about_us")}</h3>
          <div className={style.section_about}>
            <p>{about}</p>
          </div>
        </div>
      ) : null}
    </>
  );
};

export default ShowroomSocial;

type ComponentPropsType = {
  about: string;
  branches?: {
    id: number;
    name: string;
    address: string;
    phone: string;
    map_link: string;
  }[];
  social?: {
    fb?: string;
    ig?: string;
    yt?: string;
    website?: string;
  };
} & (
  | {
      isServiceCenter: true;
      daysOff?: string;
      working_hours?: string;
      address: string;
      region: {
        name: string;
        city: {
          name: string;
        };
      };
    }
  | {
      isServiceCenter: false;
    }
);
